import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

// 🔹 Reusable Try It Button
function TryItButton({ code }) {
  const handleClick = () => {
    const encodedCode = encodeURIComponent(code);
    window.open(`/editorJS?code=${encodedCode}`, "_blank");
  };

  return (
    <button onClick={handleClick} className="try_it_button">
      ▶ Try It
    </button>
  );
}

export default function JS_Arithmetic_Operators() {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">➗ JavaScript Arithmetic Operators</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        JavaScript arithmetic operators are used to perform mathematical
        operations such as addition, subtraction, multiplication, division, and
        more. These are the basic building blocks for calculations in any
        program.
      </p>

      {/* Key Points Table */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
        }}
      >
        <h2 style={{ color: "#075985", marginBottom: "1rem" }}>
          🔑 Key Points
        </h2>
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "1.1rem",
            backgroundColor: "white",
            borderRadius: "8px",
            overflow: "hidden",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <thead style={{ backgroundColor: "#e2e8f0" }}>
            <tr>
              <th style={{ padding: "12px", textAlign: "left" }}>Operator</th>
              <th style={{ padding: "12px", textAlign: "left" }}>
                Description
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ padding: "10px" }}>+</td>
              <td style={{ padding: "10px" }}>Addition</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>-</td>
              <td style={{ padding: "10px" }}>Subtraction</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>*</td>
              <td style={{ padding: "10px" }}>Multiplication</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>**</td>
              <td style={{ padding: "10px" }}>
                Exponentiation <small>(ES2016)</small>
              </td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>/</td>
              <td style={{ padding: "10px" }}>Division</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>%</td>
              <td style={{ padding: "10px" }}>Modulus (Remainder)</td>
            </tr>
            <tr>
              <td style={{ padding: "10px" }}>++</td>
              <td style={{ padding: "10px" }}>Increment</td>
            </tr>
            <tr style={{ backgroundColor: "#f9fafb" }}>
              <td style={{ padding: "10px" }}>--</td>
              <td style={{ padding: "10px" }}>Decrement</td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* Example 1: Addition and Subtraction */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Addition and Subtraction</h2>
        <p>
          The <code>+</code> operator adds values, while the <code>-</code>{" "}
          operator subtracts values.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 15;
let y = 7;
console.log("Addition:", x + y);
console.log("Subtraction:", x - y);`}
          />
          <TryItButton
            code={`let x = 15;
let y = 7;
console.log("Addition:", x + y);
console.log("Subtraction:", x - y);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #2563eb",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Addition: 22
            <br />
            Subtraction: 8
          </pre>
        </div>
      </div>

      {/* Example 2: Multiplication and Division */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Multiplication and Division</h2>
        <p>
          Use <code>*</code> for multiplication and <code>/</code> for division.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let a = 10;
let b = 5;
console.log("Multiplication:", a * b);
console.log("Division:", a / b);`}
          />
          <TryItButton
            code={`let a = 10;
let b = 5;
console.log("Multiplication:", a * b);
console.log("Division:", a / b);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #16a34a",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Multiplication: 50
            <br />
            Division: 2
          </pre>
        </div>
      </div>

      {/* Example 3: Modulus Operator */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Modulus Operator</h2>
        <p>
          The <code>%</code> operator gives the remainder after division.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let num1 = 17;
let num2 = 5;
console.log("Remainder:", num1 % num2);`}
          />
          <TryItButton
            code={`let num1 = 17;
let num2 = 5;
console.log("Remainder:", num1 % num2);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #9333ea",
            borderRadius: "8px",
          }}
        >
          Output: <b>Remainder: 2</b>
        </div>
      </div>

      {/* Example 4: Increment and Decrement */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ Increment and Decrement</h2>
        <p>
          The <code>++</code> increases a number by 1, while <code>--</code>{" "}
          decreases it by 1.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let count = 10;
count++;
console.log("After Increment:", count);
count--;
console.log("After Decrement:", count);`}
          />
          <TryItButton
            code={`let count = 10;
count++;
console.log("After Increment:", count);
count--;
console.log("After Decrement:", count);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #f59e0b",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            After Increment: 11
            <br />
            After Decrement: 10
          </pre>
        </div>
      </div>

      {/* Example 5: Combined Example */}
      <div className="tutorial-section">
        <h2 className="section-title">5️⃣ Combined Example</h2>
        <p>
          Let’s combine all arithmetic operators into a single example for
          better understanding.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let x = 8;
let y = 3;
console.log("Addition:", x + y);
console.log("Subtraction:", x - y);
console.log("Multiplication:", x * y);
console.log("Division:", x / y);
console.log("Remainder:", x % y);
x++;
console.log("Incremented:", x);
y--;
console.log("Decremented:", y);`}
          />
          <TryItButton
            code={`let x = 8;
let y = 3;
console.log("Addition:", x + y);
console.log("Subtraction:", x - y);
console.log("Multiplication:", x * y);
console.log("Division:", x / y);
console.log("Remainder:", x % y);
x++;
console.log("Incremented:", x);
y--;
console.log("Decremented:", y);`}
          />
        </div>

        <div
          style={{
            marginTop: "1rem",
            fontSize: "1.2rem",
            padding: "10px",
            border: "2px dashed #3b82f6",
            borderRadius: "8px",
          }}
        >
          Output:
          <pre>
            Addition: 11
            <br />
            Subtraction: 5
            <br />
            Multiplication: 24
            <br />
            Division: 2.6667
            <br />
            Remainder: 2
            <br />
            Incremented: 9
            <br />
            Decremented: 2
          </pre>
        </div>
      </div>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>Assignment Operators</b> to combine arithmetic with
            assignment.
          </li>
          <li>
            Explore <b>Comparison Operators</b> to compare values.
          </li>
          <li>Build small programs using multiple arithmetic operations.</li>
        </ul>
      </div>
    </div>
  );
}
